/**
 * Lazy loader for Doka Image Editor
 * Only loads the 446KB library when actually needed for image editing
 */

let dokaModule = null
let dokaLoadingPromise = null
let dokaCssLoaded = false

/**
 * Dynamically loads Doka CSS
 */
function loadDokaCSS() {
  if (dokaCssLoaded) return Promise.resolve()
  
  // Import CSS dynamically using Vite's import
  return Promise.all([
    import('src/dist/doka/doka.css'),
    import('src/dist/doka/doka-mobile.css')
  ]).then(() => {
    dokaCssLoaded = true
  }).catch((error) => {
    console.error('Failed to load Doka CSS:', error)
    // Fallback: try loading via link tag
    return new Promise((resolve, reject) => {
      // Check if CSS is already in the DOM
      if (document.querySelector('link[href*="doka.css"]')) {
        dokaCssLoaded = true
        resolve()
        return
      }

      // Load main CSS
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = '/src/dist/doka/doka.css'
      link.onload = () => {
        // Load mobile CSS
        const mobileLink = document.createElement('link')
        mobileLink.rel = 'stylesheet'
        mobileLink.href = '/src/dist/doka/doka-mobile.css'
        mobileLink.onload = () => {
          dokaCssLoaded = true
          resolve()
        }
        mobileLink.onerror = reject
        document.head.appendChild(mobileLink)
      }
      link.onerror = reject
      document.head.appendChild(link)
    })
  })
}

/**
 * Dynamically loads Doka Image Editor module
 * @returns {Promise<{create: Function}>} Promise that resolves to Doka module with create function
 */
export async function loadDoka() {
  // Return cached module if already loaded
  if (dokaModule) {
    return dokaModule
  }

  // Return existing loading promise if already loading
  if (dokaLoadingPromise) {
    return dokaLoadingPromise
  }

  // Start loading both CSS and JS
  dokaLoadingPromise = Promise.all([
    loadDokaCSS(),
    import('src/dist/doka/doka.jsx').then((module) => {
      dokaModule = module
      return module
    })
  ]).then(([, module]) => {
    dokaLoadingPromise = null
    return module
  }).catch((error) => {
    dokaLoadingPromise = null
    console.error('Failed to load Doka Image Editor:', error)
    throw error
  })

  return dokaLoadingPromise
}

/**
 * Creates a Doka editor instance with the given options
 * This function loads Doka on-demand
 * @param {Object} options - Doka editor options
 * @returns {Promise<Object>} Promise that resolves to Doka editor instance
 */
export async function createDokaEditor(options = {}) {
  const doka = await loadDoka()

  // Default configuration with proper markup/text support
  const defaultOptions = {
    // Enable all utilities including markup
    utils: ['crop', 'filter', 'color', 'markup'],
    
    // Markup configuration for text editing
    markupAllowAddMarkup: true,
    markupAllowCustomColor: true,
    
    // Font options for text markup
    markupFontFamily: 'Helvetica, Arial, Verdana',
    markupFontFamilyOptions: [
      ['Helvetica, Arial, Verdana', 'Helvetica'],
      ['Georgia, Times New Roman, serif', 'Georgia'],
      ['Courier New, monospace', 'Courier'],
      ['Impact, Haettenschweiler, sans-serif', 'Impact'],
    ],
    
    // Font size options
    markupFontSize: 0.08,
    markupFontSizeOptions: [
      [0.04, 'Small'],
      [0.08, 'Medium'],
      [0.12, 'Large'],
      [0.18, 'Extra Large'],
    ],
    
    // Color options for markup
    markupColorOptions: [
      ['#000000', 'Black'],
      ['#FFFFFF', 'White'],
      ['#FF0000', 'Red'],
      ['#00FF00', 'Green'],
      ['#0000FF', 'Blue'],
      ['#FFFF00', 'Yellow'],
      ['#FF6600', 'Orange'],
      ['#8B5CF6', 'Purple'],
    ],

    // NOTE: do NOT set Doka's `size` option here for "mobile fullscreen". `size` is the output
    // RESIZE target (GET_SIZE → the Resize util), not an editor layout/viewport option. Setting it
    // to the screen's pixel dimensions made the editor try to resize the output to e.g. 390×844,
    // which conflicts with cropAspectRatio and makes PREPARE_OUTPUT fail on confirm — so the tick
    // (Done) silently did nothing on mobile. The mobile fullscreen layout is handled entirely by
    // doka-mobile.css.
  }
  
  // Merge with user-provided options (user options take precedence)
  const mergedOptions = { ...defaultOptions, ...options }
  
  return doka.create(mergedOptions)
}

/**
 * Preloads Doka in the background (useful for when you know user might edit soon)
 * This doesn't block the page load
 */
export function preloadDoka() {
  // Load in the background without awaiting
  loadDoka().catch(() => {
    // Silently fail - it will load when actually needed
  })
}

